import { useEffect, useId, useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react'
import { compressImage } from './lib/compressImage.ts'
import { trackEvent } from './tracking.ts'
import type { DiagnoseCategory, DiagnoseResult, DiagnoseUrgency } from './types/diagnose.ts'

const JOIN_URL = 'https://bloomagrows.com/join'
const SHOP_PESTS = 'https://bloomagrows.com/shop/20-Common-Garden-Pests'
const SHOP_SOIL = 'https://bloomagrows.com/shop/soil-mix-recipe'
const LOADING_LINES = [
  'Squinting at the leaves…',
  'Checking for pests, thirst, and drama…',
  'Asking the tomatoes what they saw…',
]

const CATEGORY_LABEL: Record<DiagnoseCategory, string> = {
  pest: 'Pests',
  disease: 'Disease',
  watering: 'Water',
  nutrients: 'Nutrients',
  environment: 'Sun, soil, or weather',
  healthy: 'Looking okay',
  'not-a-plant': 'Not a plant',
  unclear: 'Unclear',
}

const URGENCY_LABEL: Record<DiagnoseUrgency, string> = {
  chill: 'No panic',
  'act-soon': 'Act this week',
  asap: 'Don’t wait',
}

function BloomaMark() {
  return (
    <a className="wtf-mark" href="https://bloomagrows.com/">
      <img src="/blooma-icon.svg" alt="" width={36} height={36} />
      <span>Blooma</span>
    </a>
  )
}

function resourceFor(category: DiagnoseCategory) {
  if (category === 'pest' || category === 'disease') {
    return { href: SHOP_PESTS, label: 'Open the garden pests guide' }
  }
  if (category === 'nutrients' || category === 'environment') {
    return { href: SHOP_SOIL, label: 'See the soil mix recipe' }
  }
  return null
}

function ExternalCta({
  href,
  className,
  children,
}: {
  href: string
  className: string
  children: string
}) {
  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  )
}

async function diagnosePhoto(image: string, notes: string) {
  const response = await fetch('/api/diagnose-plant', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image, notes }),
  })
  const payload = (await response.json().catch(() => null)) as
    | (DiagnoseResult & { error?: string })
    | null
  if (!response.ok) {
    throw new Error(payload?.error || 'Could not diagnose that photo. Try again.')
  }
  if (!payload || !Array.isArray(payload.whatToDo)) {
    throw new Error('Could not diagnose that photo. Try again.')
  }
  return payload as DiagnoseResult
}

export function WtfPage() {
  const fileId = useId()
  const cameraId = useId()
  const notesId = useId()
  const fileRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [image, setImage] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [loadingLine, setLoadingLine] = useState(LOADING_LINES[0])
  const [error, setError] = useState('')
  const [result, setResult] = useState<DiagnoseResult | null>(null)

  useEffect(() => {
    if (!result) return
    document.getElementById('wtf-result')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [result])

  async function acceptFile(file: File | undefined) {
    if (!file) return
    setError('')
    setResult(null)
    try {
      const compressed = await compressImage(file)
      setPreview(compressed.dataUrl)
      setImage(compressed.dataUrl)
    } catch (err) {
      setPreview(null)
      setImage(null)
      setError(err instanceof Error ? err.message : 'Could not read that photo.')
    }
  }

  function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    void acceptFile(file)
    event.target.value = ''
  }

  function onDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault()
    void acceptFile(event.dataTransfer.files?.[0])
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!image) {
      setError('Add a photo of the plant first.')
      return
    }
    setBusy(true)
    setError('')
    setResult(null)
    setLoadingLine(LOADING_LINES[Math.floor(Math.random() * LOADING_LINES.length)])
    trackEvent('diagnose_start')
    try {
      const diagnosis = await diagnosePhoto(image, notes)
      setResult(diagnosis)
      trackEvent('diagnose_success', {
        category: diagnosis.category,
        urgency: diagnosis.urgency,
        plant: diagnosis.plantGuess,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not diagnose that photo.'
      setError(message)
      trackEvent('diagnose_error')
    } finally {
      setBusy(false)
    }
  }

  function reset() {
    setPreview(null)
    setImage(null)
    setNotes('')
    setResult(null)
    setError('')
  }

  const resource = result ? resourceFor(result.category) : null

  return (
    <div className="wtf">
      <header className="wtf-top">
        <div className="wtf-top__inner">
          <BloomaMark />
          <p className="wtf-top__tag">Plant troubleshooter</p>
          <a className="wtf-cta wtf-cta--sm" href={JOIN_URL}>
            Join club →
          </a>
        </div>
      </header>

      <main>
        <section className="wtf-hero">
          <div className="wtf-wrap">
            <p className="wtf-eyebrow">free · snap a photo · get a plan</p>
            <h1>
              <span className="wtf-hero__wtf">WTF</span> is wrong with my plant?
            </h1>
            <p className="wtf-lede">
              Upload a picture of the sad leaf, weird spots, or “is this even
              alive?” moment. We’ll tell you what’s going on — and what to do
              next.
            </p>
          </div>
        </section>

        <section className="wtf-panel" aria-label="Plant diagnosis form">
          <form className="wtf-wrap wtf-card" onSubmit={onSubmit}>
            <input
              ref={fileRef}
              id={fileId}
              className="wtf-file"
              type="file"
              accept="image/*"
              aria-label="Upload a plant photo"
              onChange={onFile}
            />
            <input
              ref={cameraRef}
              id={cameraId}
              className="wtf-file"
              type="file"
              accept="image/*"
              capture="environment"
              aria-label="Take a plant photo"
              onChange={onFile}
            />

            <button
              type="button"
              className={preview ? 'wtf-drop wtf-drop--has' : 'wtf-drop'}
              onClick={() => fileRef.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={onDrop}
            >
              {preview ? (
                <img src={preview} alt="Your plant photo" />
              ) : (
                <>
                  <strong>Drop a photo here</strong>
                  <span>or tap to upload from your camera roll</span>
                </>
              )}
            </button>

            <div className="wtf-actions">
              <button
                type="button"
                className="wtf-cta wtf-cta--ghost"
                onClick={() => cameraRef.current?.click()}
              >
                Take a photo
              </button>
              <button
                type="button"
                className="wtf-cta wtf-cta--ghost"
                onClick={() => fileRef.current?.click()}
              >
                Upload
              </button>
              {preview ? (
                <button type="button" className="wtf-text-btn" onClick={reset}>
                  Clear
                </button>
              ) : null}
            </div>

            <label className="wtf-notes" htmlFor={notesId}>
              What’s bugging you? <span>(optional)</span>
              <textarea
                id={notesId}
                value={notes}
                maxLength={500}
                rows={3}
                placeholder="Yellow leaves, holes, white fuzz, it just looks sad…"
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>

            <button className="wtf-cta wtf-cta--wide" type="submit" disabled={busy}>
              {busy ? loadingLine : 'Tell me what’s wrong →'}
            </button>

            {error ? (
              <p className="wtf-error" role="alert">
                {error}
              </p>
            ) : null}
          </form>
        </section>

        {result ? (
          <section className="wtf-result" id="wtf-result" aria-live="polite">
            <div className="wtf-wrap wtf-card wtf-card--result">
              <div className="wtf-pills">
                <span className={`wtf-pill wtf-pill--${result.urgency}`}>
                  {URGENCY_LABEL[result.urgency]}
                </span>
                <span className="wtf-pill">{CATEGORY_LABEL[result.category]}</span>
                <span className="wtf-pill">
                  {result.plantGuess}
                  {result.plantConfidence === 'low' ? ' (guess)' : ''}
                </span>
              </div>
              <h2>{result.headline}</h2>
              {result.summary ? <p className="wtf-summary">{result.summary}</p> : null}
              {result.likelyCause ? (
                <div className="wtf-block">
                  <h3>Most likely cause</h3>
                  <p>{result.likelyCause}</p>
                </div>
              ) : null}
              <div className="wtf-block">
                <h3>What to do</h3>
                <ol>
                  {result.whatToDo.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
              {result.watchFor ? (
                <div className="wtf-block">
                  <h3>Watch for</h3>
                  <p>{result.watchFor}</p>
                </div>
              ) : null}
              {result.otherPossibilities.length > 0 ? (
                <div className="wtf-block">
                  <h3>Could also be</h3>
                  <ul>
                    {result.otherPossibilities.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="wtf-cta-row">
                {resource ? (
                  <ExternalCta className="wtf-cta wtf-cta--wide" href={resource.href}>
                    {resource.label}
                  </ExternalCta>
                ) : null}
                <ExternalCta
                  className={
                    resource
                      ? 'wtf-cta wtf-cta--ghost wtf-cta--wide'
                      : 'wtf-cta wtf-cta--wide'
                  }
                  href={JOIN_URL}
                >
                  Want Erin beside you all season? Join Fall Garden Club — $19
                </ExternalCta>
              </div>
            </div>
          </section>
        ) : null}

        <section className="wtf-how">
          <div className="wtf-wrap">
            <h2>How it works</h2>
            <ol className="wtf-steps">
              <li>
                <strong>1. Photo the problem</strong>
                <span>Get close. Leaves, spots, and soil tell more than a wide bed shot.</span>
              </li>
              <li>
                <strong>2. We look it over</strong>
                <span>Pests, thirst, sun, and beginner-classic issues — not a lab report.</span>
              </li>
              <li>
                <strong>3. You get a plan</strong>
                <span>What it probably is, what to do this week, and when to actually worry.</span>
              </li>
            </ol>
          </div>
        </section>
      </main>

      <footer className="wtf-foot">
        <div className="wtf-wrap">
          <p>
            This is a garden coach guess from a photo, not a lab diagnosis. If a
            food crop looks like a serious blight and it’s spreading fast, check
            with your local extension office.
          </p>
          <p>
            <a href="https://bloomagrows.com/">bloomagrows.com</a>
            {' · '}
            <a href="https://instagram.com/theblohmstead">@theblohmstead</a>
          </p>
        </div>
      </footer>
    </div>
  )
}
